import React, { useRef, useEffect, useState } from 'react';
import { GraduationCap } from 'lucide-react';

function CourseCard({ course, index }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 0.08}s` }}
    >
      <div className="p-5 lg:p-6 rounded-2xl glass border border-white/[0.04] hover:border-white/[0.08] transition-all duration-500 h-full hover:translate-y-[-2px]">
        {/* Header with colored dot */}
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-2.5 h-2.5 rounded-full animate-pulse"
            style={{ backgroundColor: course.color }}
          />
          <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">
            {course.year}
          </span>
        </div>

        {/* Course title */}
        <h3 className="font-display font-bold text-sm lg:text-base text-white mb-3 leading-relaxed line-clamp-2 min-h-[2.8em]">
          {course.title}
        </h3>

        {/* Institution */}
        <div className="flex items-center gap-2 mt-auto">
          <GraduationCap className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
          <span className="text-xs text-gray-500">{course.institution}</span>
        </div>

        {/* Bottom gradient line on hover */}
        <div
          className="mt-4 h-px w-0 group-hover:w-full transition-all duration-500 rounded-full"
          style={{
            background: `linear-gradient(90deg, ${course.color}, transparent)`,
          }}
        />
      </div>
    </div>
  );
}

export default function Courses({ courses, visible }) {
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      setTimeout(() => setTitleVisible(true), 100);
    } else {
      setTitleVisible(false);
    }
  }, [visible]);

  return (
    <section className="relative min-h-screen flex items-center px-6 lg:px-12 py-24 lg:py-32">
      <div className="max-w-6xl mx-auto w-full">
        {/* Section header */}
        <div
          className={`text-center mb-12 lg:mb-16 transition-all duration-700 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-3 mb-4 justify-center">
            <div className="w-8 h-[2px] bg-gradient-to-r from-accent-cyan to-accent-blue rounded-full" />
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">
              Formación
            </span>
            <div className="w-8 h-[2px] bg-gradient-to-r from-accent-blue to-accent-cyan rounded-full" />
          </div>
          <h2 className="font-display font-bold text-3xl lg:text-5xl text-white mb-4">
            Cursos &{' '}
            <span className="text-gradient">Certificaciones</span>
          </h2>
          <p className="text-sm lg:text-base text-gray-500">
            Formación continua 2018-2025
          </p>
        </div>

        {/* Courses grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
          {courses.map((course, index) => (
            <CourseCard key={index} course={course} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
